import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import {
  CUTTYSARK_PHASES,
  cuttysarkRevealedTopRank,
  nextCuttysarkRevealRank,
  type CuttysarkLiveView,
} from '@cortex/domain-product-innovation-lab-types';
import { useControlCuttysarkSession } from '../../../hooks/useCuttysark.js';

/** @graph-connects none */
interface ControlPanelProps {
  sessionId: string;
  view: CuttysarkLiveView;
}

/**
 * 運営の進行パネル。状態の切り替え・発表者の切り替え・部門ごとの結果発表（3 位 → 2 位 → 1 位を
 * 1 つずつ。どこまで開くかは API が決める）・投票状況を扱う。
 * どの遷移を許すかは API が決める（ここで状態ボタンを出し分けない）。弾かれたら API の
 * メッセージをそのまま出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business CuttySark 賞セッションの進行（状態・発表者・結果めくり）を運営が操作し、投票状況を確認する
 * @graph-connects useControlCuttysarkSession [calls] 進行操作
 */
export function ControlPanel({ sessionId, view }: ControlPanelProps): ReactNode {
  const { t } = useTranslation();
  const control = useControlCuttysarkSession(sessionId);
  const operatorView = view.operatorView;

  const presenterOptions = [
    { value: '', label: t('cuttysark.operate.presenterNone') },
    ...view.candidates.map((c) => ({
      value: c.email,
      label: c.title ? `${c.title} ${c.name}` : c.name,
    })),
  ];

  return (
    <div className="space-y-5">
      <section className="space-y-2">
        <p className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('cuttysark.operate.currentPhase', { phase: t(`cuttysark.phase.${view.phase}`) })}
        </p>
        <div className="flex flex-wrap gap-2">
          {CUTTYSARK_PHASES.map((phase) => (
            <Button
              key={phase}
              type="button"
              size="sm"
              variant={phase === view.phase ? 'primary' : 'secondary'}
              disabled={phase === view.phase || control.isPending}
              onClick={() => {
                control.mutate({ phase });
              }}
            >
              {t(`cuttysark.phase.${phase}`)}
            </Button>
          ))}
        </div>
      </section>

      <section className="max-w-md">
        <Select
          label={t('cuttysark.operate.presenter')}
          options={presenterOptions}
          value={view.currentCandidateEmail ?? ''}
          onChange={(e) => {
            control.mutate({
              currentCandidateEmail: e.target.value === '' ? null : e.target.value,
            });
          }}
        />
      </section>

      {view.phase === 'revealing' && (
        <section className="space-y-2">
          <p className="text-xs text-[var(--color-text-muted)]">
            {t('cuttysark.operate.revealHelp')}
          </p>
          {view.divisions.map((division) => {
            const entries = view.results.find((r) => r.divisionId === division.id)?.entries ?? [];
            const count = view.reveal.find((r) => r.divisionId === division.id)?.count ?? 0;
            const shownRank = cuttysarkRevealedTopRank(entries, count);
            const nextRank = nextCuttysarkRevealRank(entries, count);
            return (
              <div key={division.id} className="flex flex-wrap items-center gap-3">
                <span className="w-40 truncate text-sm font-semibold text-[var(--color-text-primary)]">
                  {division.name}
                </span>
                <span className="text-sm text-[var(--color-text-muted)]">
                  {shownRank === null
                    ? t('cuttysark.operate.revealNone')
                    : t('cuttysark.operate.revealShown', { rank: shownRank })}
                </span>
                <Button
                  type="button"
                  size="sm"
                  disabled={nextRank === null || control.isPending}
                  onClick={() => {
                    control.mutate({ revealStep: { divisionId: division.id, direction: 'next' } });
                  }}
                >
                  {nextRank === null
                    ? t('cuttysark.operate.revealDone')
                    : t('cuttysark.operate.revealNext', { rank: nextRank })}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={shownRank === null || control.isPending}
                  onClick={() => {
                    control.mutate({ revealStep: { divisionId: division.id, direction: 'back' } });
                  }}
                >
                  {t('cuttysark.operate.revealBack')}
                </Button>
              </div>
            );
          })}
        </section>
      )}

      {control.isError && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
          {control.error.message}
        </p>
      )}

      {operatorView !== null && (
        <section className="space-y-1">
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {t('cuttysark.operate.progressHeading')}
          </h3>
          <p className="text-sm text-[var(--color-text-secondary)]">
            {t('cuttysark.operate.submittedCount', { count: operatorView.submittedVoters.length })}
          </p>
          {operatorView.voterMode === 'listed' &&
            (operatorView.pendingVoters.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)]">
                {t('cuttysark.operate.noPending')}
              </p>
            ) : (
              <div>
                <p className="text-xs text-[var(--color-text-muted)]">
                  {t('cuttysark.operate.pending')}
                </p>
                <ul className="mt-1 flex flex-wrap gap-1.5">
                  {operatorView.pendingVoters.map((email) => (
                    <li
                      key={email}
                      className="rounded-full bg-[var(--color-warning-muted)] px-2.5 py-1 text-xs text-[var(--color-text-primary)]"
                    >
                      {email}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
        </section>
      )}
    </div>
  );
}
